import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import type { ComboBoxItem, ComboBoxProps } from "./types";

const IOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

/**
 * Combobox: static `items` (client-side filter) or async `searchFunction` (debounced).
 * Ported from `src/lib/comboBox/components/comboBox.svelte`.
 */
export function ComboBox({
  items: itemsProp = [],
  searchFunction,
  onSelectItem,
  onAddNew,
  placeholder = "Buscar…",
  disabled = false,
  addNewLabel = "Crear nuevo",
  showAddNew = true,
  label = "",
  hideLabel = false,
  required = false,
  resetInput = false,
  onResetInput,
  personaView = false,
  selectedValue = null,
  maxVisibleItems = 6,
  unlimitedOptions = false,
  useFixedPosition = false,
  errorText = "",
  variant = "default",
  className = "",
  id: idProp,
  inputId: inputIdProp,
  "aria-labelledby": ariaLabelledBy,
}: ComboBoxProps) {
  const reactId = useId();
  const baseId = inputIdProp || idProp || `combo-${reactId.replace(/:/g, "")}`;
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState<ComboBoxItem[]>([]);
  const [opensAbove, setOpensAbove] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
  );
  const [sheetQuery, setSheetQuery] = useState("");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sheetInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<(() => void) | null>(null);
  /** Re-measure when panel height was 0 on first paint (fixed + open above) */
  const positionRetryRef = useRef(0);

  const hasStatic = Array.isArray(itemsProp) && itemsProp.length > 0;
  const showError = Boolean(errorText) || variant === "error";
  const showSuccess = variant === "success";

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (isMobile && showDropdown) {
      setSheetQuery(searchQuery);
      const t = setTimeout(() => sheetInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [isMobile, showDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!resetInput) {
      return;
    }
    setSearchQuery("");
    setShowDropdown(false);
    onResetInput?.();
  }, [resetInput, onResetInput]);

  useEffect(() => {
    if (selectedValue && selectedValue.label) {
      setSearchQuery(String(selectedValue.label));
    } else if (selectedValue == null) {
      setSearchQuery("");
    }
  }, [selectedValue?.label, selectedValue]);

  const runStaticFilter = useCallback(
    (q: string) => {
      const list = hasStatic ? [...itemsProp] : [];
      if (!q.trim()) {
        return list;
      }
      return list.filter((item) =>
        item.label.toLowerCase().includes(q.toLowerCase().trim())
      );
    },
    [hasStatic, itemsProp]
  );

  const positionDropdown = useCallback(() => {
    const inputEl = inputRef.current;
    const dropEl = dropdownRef.current;
    if (!inputEl) return;
    const inputRect = inputEl.getBoundingClientRect();
    const vh = window.innerHeight;
    /** ~max-h-60 (15rem); used only to decide above vs below */
    const approxMaxListH = 240;
    const spaceBelow = vh - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const openAbove =
      spaceBelow < approxMaxListH && spaceAbove > spaceBelow;
    setOpensAbove(openAbove);

    const GAP = 4;

    if (useFixedPosition) {
      if (openAbove) {
        const dh = dropEl?.getBoundingClientRect().height ?? 0;
        const hFallback = approxMaxListH;
        const h = dh >= 8 ? dh : hFallback;
        /** Bottom of panel sits GAP px above the top of the input */
        const top = inputRect.top - GAP - h;
        setDropPos({
          top: Math.max(4, top),
          left: inputRect.left,
          width: inputRect.width,
        });
        if (dh < 8 && positionRetryRef.current < 16) {
          positionRetryRef.current += 1;
          requestAnimationFrame(() => positionDropdown());
        } else {
          positionRetryRef.current = 0;
        }
      } else {
        positionRetryRef.current = 0;
        setDropPos({
          top: inputRect.bottom + GAP,
          left: inputRect.left,
          width: inputRect.width,
        });
      }
    } else if (dropEl) {
      if (openAbove) {
        dropEl.style.top = "auto";
        dropEl.style.bottom = "100%";
        dropEl.style.marginBottom = "-1px";
        dropEl.style.marginTop = "0";
      } else {
        dropEl.style.top = "100%";
        dropEl.style.bottom = "auto";
        dropEl.style.marginTop = "-1px";
        dropEl.style.marginBottom = "0";
      }
    }
  }, [useFixedPosition]);

  const handleInput: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (hasStatic) {
      setFilteredItems(runStaticFilter(v));
      setShowDropdown(true);
    } else if (searchFunction) {
      debounceRef.current = setTimeout(async () => {
        const res = await searchFunction(v);
        if (Array.isArray(res)) {
          setFilteredItems(res);
          setShowDropdown(true);
        }
      }, 150);
    } else {
      setFilteredItems([]);
    }
  };

  const openAndPrimeList = useCallback(() => {
    setShowDropdown(true);
    if (isMobile) {
      // Blur immediately — the bottom sheet has its own search input
      requestAnimationFrame(() => inputRef.current?.blur());
      return;
    }
    if (hasStatic) {
      setFilteredItems(runStaticFilter(searchQuery));
    } else if (searchFunction) {
      setFilteredItems([]);
    }
  }, [hasStatic, runStaticFilter, searchQuery, searchFunction, isMobile]);

  const selectItem = (item: ComboBoxItem) => {
    setSearchQuery(item.label);
    setShowDropdown(false);
    onSelectItem?.(item);
    inputRef.current?.blur();
  };

  const handleAddNew = () => {
    onAddNew?.(searchQuery);
    setShowDropdown(false);
    inputRef.current?.blur();
  };

  const onBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (isMobile) return; // bottom sheet manages its own closing
    const rel = e.relatedTarget as Node | null;
    if (dropdownRef.current && rel && dropdownRef.current.contains(rel)) {
      return;
    }
    const delay = 300;
    window.setTimeout(() => {
      setShowDropdown(false);
    }, delay);
  };

  const closeSheet = useCallback(() => {
    setShowDropdown(false);
    setSheetQuery("");
  }, []);

  const visibleList = useMemo(
    () =>
      unlimitedOptions
        ? filteredItems
        : filteredItems.slice(0, maxVisibleItems),
    [filteredItems, maxVisibleItems, unlimitedOptions]
  );

  const sheetVisibleList = useMemo(() => {
    if (!hasStatic) return filteredItems; // async: reuse server results
    const list = itemsProp as ComboBoxItem[];
    if (!sheetQuery.trim()) return list;
    return list.filter((item) =>
      item.label.toLowerCase().includes(sheetQuery.toLowerCase().trim())
    );
  }, [hasStatic, itemsProp, sheetQuery, filteredItems]);

  useEffect(() => {
    if (!showDropdown) {
      positionRetryRef.current = 0;
    }
  }, [showDropdown]);

  useLayoutEffect(() => {
    if (!showDropdown) {
      return;
    }
    let id1 = 0;
    const id0 = requestAnimationFrame(() => {
      id1 = requestAnimationFrame(() => {
        positionDropdown();
      });
    });
    return () => {
      cancelAnimationFrame(id0);
      cancelAnimationFrame(id1);
    };
  }, [showDropdown, filteredItems, positionDropdown]);

  useLayoutEffect(() => {
    if (!showDropdown || !useFixedPosition) {
      return;
    }
    const el = dropdownRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(() => {
      positionRetryRef.current = 0;
      positionDropdown();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [showDropdown, useFixedPosition, positionDropdown, visibleList.length]);

  useEffect(() => {
    if (!useFixedPosition || !showDropdown) {
      if (scrollRef.current) {
        window.removeEventListener("scroll", scrollRef.current, true);
        window.removeEventListener("resize", scrollRef.current);
        scrollRef.current = null;
      }
      return;
    }
    const u = () => positionDropdown();
    window.addEventListener("scroll", u, true);
    window.addEventListener("resize", u);
    scrollRef.current = u;
    return () => {
      window.removeEventListener("scroll", u, true);
      window.removeEventListener("resize", u);
      scrollRef.current = null;
    };
  }, [useFixedPosition, showDropdown, positionDropdown]);

  useEffect(() => {
    if (!showDropdown) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowDropdown(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showDropdown]);

  const inputClass = useMemo(
    () =>
      [
        "block w-full min-h-[44px] rounded-lg border pr-10 text-base text-slate-900 transition outline-none",
        "font-inherit pl-2.5 focus:border-[#205f43] focus:ring-2 focus:ring-[#205f43]/25",
        showError
          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
          : showSuccess
            ? "border-emerald-600"
            : "border-slate-200 bg-white",
        showDropdown
          ? opensAbove
            ? "rounded-b-lg border-t-0 border-t-transparent shadow-none"
            : "rounded-t-lg border-b-0 border-b-transparent shadow-none"
          : "rounded-lg",
        disabled && "cursor-not-allowed bg-slate-100 opacity-60",
      ]
        .filter(Boolean)
        .join(" "),
    [showError, showSuccess, showDropdown, opensAbove, disabled]
  );

  const listBoxStyle: React.CSSProperties = useFixedPosition
    ? {
        position: "fixed",
        top: dropPos.top,
        left: dropPos.left,
        width: dropPos.width,
        zIndex: 100000,
      }
    : {};

  return (
    <div
      className={`relative w-full ${
        showError ? "text-red-600" : ""
      } ${className}`}
    >
      {label && !hideLabel && (
        <label
          htmlFor={baseId}
          className="mb-1 block cursor-pointer text-sm font-medium text-slate-800"
        >
          {required ? <span className="text-red-500">* </span> : null}
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={baseId}
          type="text"
          className={inputClass}
          value={searchQuery}
          onChange={handleInput}
          onFocus={openAndPrimeList}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={searchQuery ? "" : placeholder}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls={showDropdown ? `${baseId}-listbox` : undefined}
          aria-labelledby={ariaLabelledBy}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400"
          aria-hidden
        >
          <Search className="h-4 w-4" />
        </div>
      </div>

      {/* Desktop dropdown */}
      {!isMobile && showDropdown && (visibleList.length > 0 || (showAddNew && onAddNew)) ? (
        <div
          ref={dropdownRef}
          id={`${baseId}-listbox`}
          className={
            useFixedPosition
              ? "z-[100000] flex max-h-60 flex-col overflow-hidden rounded-b-lg border border-slate-200 bg-white shadow-lg"
              : `absolute z-[100000] ${
                  opensAbove
                    ? "mb-0 rounded-b-none rounded-t-lg border-b-0"
                    : "mt-0 rounded-b-lg rounded-t-none border-t-0"
                } flex w-full max-h-60 flex-col border border-slate-200 bg-white shadow-lg ${
                  opensAbove ? "bottom-full" : "top-full"
                }`
          }
          style={listBoxStyle}
          role="listbox"
          aria-label="Opciones"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {visibleList.length > 0 ? (
            <ul className="max-h-60 overflow-y-auto p-0">
              {visibleList.map((item, i) => (
                <li key={String(item.value) + i}>
                  <button
                    type="button"
                    className="w-full border-0 bg-transparent py-2 pl-3 pr-3 text-left text-sm text-slate-800 transition hover:bg-slate-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      selectItem(item);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    role="option"
                    aria-label={`Seleccionar ${item.label}`}
                  >
                    {personaView ? (
                      <div className="flex items-center gap-3 p-0.5">
                        {item.picture ? (
                          <img
                            src={String(item.picture)}
                            alt=""
                            className="h-10 w-10 shrink-0 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-medium text-slate-600">
                            {item.label.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-slate-900">
                            {item.label}
                          </div>
                          {item.phone ? (
                            <div className="truncate text-xs text-slate-500">
                              {String(item.phone)}
                            </div>
                          ) : null}
                          {item.email ? (
                            <div className="truncate text-xs text-slate-500">
                              {String(item.email)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <span className="block">{item.label}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {showAddNew && onAddNew ? (
            <div className="sticky bottom-0 border-t border-slate-200 bg-white p-2">
              <button
                type="button"
                className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-800 transition hover:bg-slate-100"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleAddNew();
                }}
              >
                {addNewLabel}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Mobile bottom sheet */}
      {isMobile && showDropdown
        ? createPortal(
            <div className="fixed inset-0 z-[200000] flex flex-col justify-end">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={closeSheet}
              />
              {/* Sheet */}
              <div className="combo-sheet relative flex max-h-[80dvh] flex-col rounded-t-2xl bg-white shadow-2xl">
                {/* Drag handle */}
                <div className="flex shrink-0 justify-center pb-1 pt-3">
                  <div className="h-1 w-10 rounded-full bg-slate-300" />
                </div>
                {/* Search bar */}
                <div className="shrink-0 px-4 pb-3 pt-1">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      ref={sheetInputRef}
                      type="text"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-9 pr-10 text-base text-slate-900 outline-none focus:border-[#205f43] focus:ring-2 focus:ring-[#205f43]/25"
                      placeholder={placeholder}
                      value={sheetQuery}
                      autoComplete="off"
                      onChange={(e) => {
                        const v = e.target.value;
                        setSheetQuery(v);
                        if (!hasStatic && searchFunction) {
                          if (debounceRef.current) clearTimeout(debounceRef.current);
                          debounceRef.current = setTimeout(async () => {
                            const res = await searchFunction(v);
                            if (Array.isArray(res)) setFilteredItems(res);
                          }, 150);
                        }
                      }}
                    />
                    {sheetQuery ? (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 active:bg-slate-100"
                        onTouchEnd={(e) => { e.preventDefault(); setSheetQuery(""); }}
                        onClick={() => setSheetQuery("")}
                        aria-label="Limpiar búsqueda"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                </div>
                {/* Options */}
                <ul
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1"
                  role="listbox"
                  aria-label="Opciones"
                >
                  {sheetVisibleList.length > 0 ? (
                    sheetVisibleList.map((item, i) => (
                      <li key={String(item.value) + i}>
                        <button
                          type="button"
                          className="w-full border-0 bg-transparent px-5 py-3.5 text-left text-base text-slate-800 active:bg-slate-100"
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selectItem(item);
                          }}
                          onClick={() => selectItem(item)}
                          role="option"
                          aria-label={`Seleccionar ${item.label}`}
                        >
                          {personaView ? (
                            <div className="flex items-center gap-3">
                              {item.picture ? (
                                <img
                                  src={String(item.picture)}
                                  alt=""
                                  className="h-10 w-10 shrink-0 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-medium text-slate-600">
                                  {item.label.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium text-slate-900">
                                  {item.label}
                                </div>
                                {item.phone ? (
                                  <div className="truncate text-xs text-slate-500">
                                    {String(item.phone)}
                                  </div>
                                ) : null}
                                {item.email ? (
                                  <div className="truncate text-xs text-slate-500">
                                    {String(item.email)}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <span>{item.label}</span>
                          )}
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-5 py-10 text-center text-sm text-slate-400">
                      Sin resultados
                    </li>
                  )}
                </ul>
                {/* Add new */}
                {showAddNew && onAddNew ? (
                  <div className="shrink-0 border-t border-slate-100 p-3">
                    <button
                      type="button"
                      className="w-full rounded-xl bg-[#205f43]/10 px-4 py-3 text-center text-sm font-semibold text-[#205f43] active:bg-[#205f43]/20"
                      onTouchEnd={(e) => { e.preventDefault(); handleAddNew(); }}
                      onClick={handleAddNew}
                    >
                      {addNewLabel}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}

      {showError && errorText ? (
        <div className="mt-1.5 text-sm text-red-600" role="alert">
          {errorText.split("\n").map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      ) : null}
      {variant === "success" ? (
        <div className="mt-1.5 text-sm text-emerald-600" role="status">
          Listo
        </div>
      ) : null}
    </div>
  );
}
